import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../services/api";
import toastOptions from "../../../utils/toastOptions";
import { TablePage } from "../../../containers";
import { currencyMask, displayDateTime } from "../../../utils/masks";
import { CouponScope } from "../../../models/enums/CouponScope";

interface CouponPerson {
  id: number;
  idEvent?: number;
  idPerson?: number;
  idEventPerson?: number;
  personName: string;
  email?: string;
  amountBefore: number;
  amountAfter: number;
  discountAmount: number;
  redeemedAt: Date;
  createdDate: Date;
  scope: string;
  idScope: number;
  idContext: number;
}

export default function CouponPersonList() {
  const { id } = useParams<'id'>();
  const location = useLocation();
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState<number>(1);
  const [couponCode, setCouponCode] = useState<string>(location.state?.couponCode || "");
  const [termSearch, setTermSearch] = useState<string>("");
  const [delayedSearchTerm, setDelayedSearchTerm] = useState("");
  const [refresh, setRefresh] = useState<boolean>(false);
  const [totalUsages, setTotalUsages] = useState<number>(0);

  const columns = [
    {
      name: "Nome",
      sortable: true,
      grow: 2,
      cell: (row: CouponPerson) => (
        <div className="uk-flex uk-flex-column uk-margin-xsmall-y">
          {(row.idPerson) ? (
            <Link
              to={`/${row.scope == CouponScope.EVENT ? "eventos" : "pedidos"}/${row.idScope}/pessoas/${row.idContext}`}
              className="uk-text-bold"
              target="_blank"
            >
              {row.personName}
            </Link>
          ) : (
            <span className="uk-text-bold">{row.personName}</span>
          )}
        </div>
      )
    },
    {
      name: "Contato",
      sortable: false,
      grow: 2,
      cell: (row: CouponPerson) => (
        <div className="uk-flex uk-flex-column uk-margin-xsmall-y">
          <span>{row.email || "-"}</span>
        </div>
      )
    },
    {
      name: "Resumo do desconto",
      sortable: true,
      cell: (row: CouponPerson) => (
        <div className="uk-flex uk-flex-column uk-margin-xsmall-y">
          <span className="uk-text-xsmall">
            {currencyMask(row.amountBefore ?? 0)}
            <span className="uk-text-muted"> - {currencyMask(row.discountAmount ?? 0)}</span>
            <span className="uk-text-muted"> = </span>
            <b className="uk-text-muted">{currencyMask(row.amountAfter ?? 0)}</b>
          </span>
        </div>
      )
    },
    {
      name: "Resgatado em",
      sortable: true,
      cell: (row: CouponPerson) => (
        <span className="uk-text-xsmall uk-text-italic" >{row.redeemedAt ? displayDateTime(row.redeemedAt) : row.createdDate ? displayDateTime(row.createdDate) : "-"}</span>
      )
    }
  ];

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(termSearch);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    setRefresh(!refresh);
  }, [delayedSearchTerm]);  

  const fetchCouponPeople = async (page: number, limit: number) => {
    try {
      const response = await api.get(`coupons/${id}/redemptions`, {
        params: {
          page: page,
          limit: limit,
          name: termSearch ? termSearch.toUpperCase() : undefined
        }
      });

      if (!couponCode) {
        const firstCode = response?.data?.data?.[0]?.code;
        if (firstCode) {
          setCouponCode(firstCode);
        }
      }

      setPageNo(page);
      setTotalUsages(response.data.total);

      return response;
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });

      setTotalUsages(0);
      return {
        data: { data: [], total: 0 }
      };
    }
  };

  const handleSearchByName = async (search: string) => {
    if (search.length > 2 || search.length === 0) {
      setTermSearch(search);
    }
  };

  return (
    <div id="page-coupon-person-list" className="uk-margin-top">
      <TablePage
        title={couponCode ? `Cupom ${couponCode}` : "Cupom"}
        subTitle={"Lista de pessoas que resgataram este cupom"}
        messageEmpty={<span>Nenhuma <b>utilização</b> encontrada para este cupom.</span>}
        fetch={fetchCouponPeople}
        columns={columns}
        pageNo={pageNo}
        refresh={refresh}
        showSubHeader
        totalRecords={totalUsages}
        labelTotalRecords={`${totalUsages} ${totalUsages === 1 ? "Utilização" : "Utilizações"}`}
        options={
          <div className="uk-grid-small uk-margin-top" uk-grid="true">
            <div className="uk-search uk-search-default uk-width-1-3@s">
              <span className="uk-search-icon-flip" uk-search-icon="true"></span>
              <input
                className="uk-search-input uk-input uk-form-small uk-text-uppercase"
                type="search"
                placeholder="Pesquise por nome..."
                style={{ textTransform: "uppercase" }}
                onChange={(ev) => {
                  handleSearchByName(ev.target.value);
                }}
              />
            </div>
          </div>
        }
        backButtonAction={() => navigate(-1)}
      />
    </div>
  );
}
